





























import Vue from 'vue'
import { Asset } from '@/types/assets'
import { KeyDict } from '@/types'
import clock from '@/classes/Clock'
import './SingleSlate.scss'

export default Vue.extend({
  name: 'SingleSlate',
  props: {
    assetId: {
      type: String,
      required: true
    },
    active: {
      type: Boolean,
      default: false
    },
    stretchcontent: {
      type: String,
      default: ''  // will be set to any value (eg 'true') if needs to stretch content
    },
    contentStyle: {
      type: Object
    },
    loop: {
      type: Boolean,
      default: true
    },
    sceneId: {
      type: String,
      required: false
    }
  },
  computed: {
    fileType (): string {
      return (this.asset !== null && this.asset.url) ? this.getFileExt(this.asset.url) : ''
    },
    isVideo (): boolean {
      const fileTypes = ['webm', 'mp4']
      return (fileTypes.includes(this.fileType))
    },
    asset (): null | Asset {
      if (!this.sceneAssets || !this.sceneAssets[this.assetId]) return null
      return this.sceneAssets[this.assetId]
    },
    isVisible (): boolean {
      if (!this.active) return false
      if (!this.loop && this.videoHasEnded) return false
      return true
    }
  },
  data: () => ({
    sceneAssets: null as null|KeyDict<Asset>,
    playInterval: null as null|NodeJS.Timeout,
    videoHasEnded: true,
    lastPlayTimestamp: 0,
    retryCounter: 0
  }),
  watch: {
    async active (newVal: boolean) {
      if (!this.isVideo) return
      if (!this.loop) {
        this.resetVideo()
      } else {
        if (newVal) await this.playVideo(true)
        else await this.playVideo(false, 0)
      }
    },
    async sceneId () {
      // Only for stingers
      if (!this.loop) this.resetVideo()
      await this.playVideo(true)
    },
    asset () {
      if (!this.loop || !this.isVisible || !this.asset) return
      if (this.playInterval !== null || !this.$refs.videoBg) return
      this.playInterval = setInterval(() => {
        if (!this.active) return
        this.playVideo(true)
      }, 1000)
    },
    videoHasEnded (newVal: boolean) {
      if (newVal) this.resetVideo()
    }
  },
  created () {
    this.sceneAssets = this.$store.direct.state.assets.sceneAssets
    // This way the stinger wont play on page reloads
    // if (this.active && this.isVideo) this.videoHasEnded = true
  },
  destroyed () {
    if (this.playInterval === null) return
    clearInterval(this.playInterval)
  },
  methods: {
    getFileExt (url: string): string {
      // eslint-disable-next-line
      // @ts-ignore
      return url.split('?')[0].split('.').pop().split(/\#|\?/)[0]
    },
    onError (event: Event) {
      this.retryCounter++
      const image = event.target as HTMLImageElement
      const retryOffset = this.retryCounter ** 2 * 1000
      if (this.retryCounter > 4) {
        console.error('Retried 4 times to properly load the slate, given up', this.asset)
        return
      }
      const message = `error trying to load the image ${image.src}, waiting for ${retryOffset}ms and try again`
      this.$store.direct.dispatch.logs.addLog({
        message,
        level: 'info'
      })
      console.warn(message)
      setTimeout(() => {
        const url = new URL(image.src)
        url.searchParams.set('counter', `${this.retryCounter}`)
        image.src = url.href
      }, retryOffset)
    },
    async playVideo (play: boolean, currentTime?: number|undefined) {
      try {
        const videoBg = this.$refs.videoBg as HTMLVideoElement
        if (currentTime !== undefined) videoBg.currentTime = currentTime
        if (this.active) {
          if (play) {
            this.lastPlayTimestamp = clock.nowInMilliseconds()
            this.videoHasEnded = false
            await videoBg.play()
          } else {
            videoBg.pause()
          }
        } else {
          this.videoHasEnded = true
        }
      } catch (error) {
        this.$store.direct.dispatch.logs.addLog({
          message: 'Could not play/pause SingleSlate video',
          level: 'error',
          data: { error }
        })
      }
    },
    onProgress () {
      if (!this.active || this.videoHasEnded || this.loop) return
      const videoBg = this.$refs.videoBg as undefined|HTMLVideoElement
      if (videoBg === undefined) return
      const videoTime = videoBg.currentTime * 1000
      const delay = clock.nowInMilliseconds() - this.lastPlayTimestamp - videoTime

      if (delay > 500 && this.$store.direct.state.events.cefEvent) {
        console.error('Delay larger than 500', {
          clock: clock.nowInMilliseconds(),
          lastPlayTimestamp: this.lastPlayTimestamp,
          videoTime: videoTime,
          delay: delay,
          assetId: this.assetId,
          asset: this.asset
        })
      }

      this.$emit('onPlaybackDelay', { delay, id: this.assetId })
    },
    resetVideo () {
      const videoBg = this.$refs.videoBg as undefined|HTMLVideoElement
      if (videoBg === undefined) return
      videoBg.pause()
      videoBg.currentTime = 0
    },
    onVideoEnded () {
      this.videoHasEnded = true
    }
  }
})
